// Wave 3 billing: the credit packs, inline over whatever is on screen. One sheet for the whole app (PacksHost in App),
// opened from anywhere:
//   openPacks({reason, needed, balance, note})   a tap, the low-balance pill, the guided tour
//   the Director's `open_packs` ui directive     ui.ts -> openPacks(directive)
//   any call answering 402 INSUFFICIENT_CREDITS  api.ts onNoCredits -> openPacksForError (App wires it)
// Buying from the sheet is POST credits/checkout -> a full redirect to Stripe; Stripe comes back to
// /api/credits/confirm and then /credits?ok=1, the same as the Credits page. Pure state here (no React, no DOM beyond
// sessionStorage, which may be missing): unit-tested in packs.test.ts.
import type { OpenPacksDirective, PacksReason } from "./types";

/** Mirrors core LOW_BALANCE_CREDITS (the web doesn't import core; packs.test.ts checks they match). */
export const LOW_BALANCE_CREDITS = 200;

export type PacksRequest = {
  reason: PacksReason;
  /** what the paid step needs, when known ("out") */
  needed?: number;
  /** their balance when it was opened, when known */
  balance?: number;
  /** a line of its own for the top of the sheet (the tour's "The character sheet is 40 credits.") */
  note?: string;
  /** who opened it, for the Director's context line ("director", "tap", "error", "tour"...) */
  source?: string;
};

let current: PacksRequest | null = null;
let opens = 0;
let closedAt = 0;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

/** Open the packs sheet (a second open replaces the first, keeping a known `needed` if the new one has none). */
export function openPacks(req: Partial<PacksRequest> = {}): void {
  const reason: PacksReason = req.reason ?? "browse";
  const prev = current;
  current = { ...req, reason, ...(req.needed === undefined && prev?.needed !== undefined && reason === "out" ? { needed: prev.needed } : {}) };
  opens++;
  emit();
}
export function closePacks(): void {
  if (!current) return;
  current = null;
  closedAt = Date.now();
  emit();
}
export const packsRequest = (): PacksRequest | null => current;
/** Changes every time the sheet is (re)opened: a key for the host, so a fresh open resets its buttons. */
export const packsOpens = () => opens;
export const packsOpen = () => current !== null;
export function subscribePacks(f: () => void): () => void {
  subs.add(f);
  return () => void subs.delete(f);
}

/** The Director's `open_packs` directive as a request (unknown reasons are "browse"; numbers must be numbers). */
export function packsFromDirective(d: Partial<OpenPacksDirective> | null | undefined): PacksRequest {
  const reason: PacksReason = d?.reason === "out" || d?.reason === "low" || d?.reason === "tour" ? d.reason : "browse";
  const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.round(v) : undefined);
  const needed = n(d?.needed);
  const balance = n(d?.balance);
  return { reason, source: "director", ...(needed !== undefined ? { needed } : {}), ...(balance !== undefined ? { balance } : {}) };
}

/** How long after the user closes the sheet an error doesn't pop it straight back (two failed calls at once). */
export const REOPEN_QUIET_MS = 2500;

/** A 402 from a paid call (INSUFFICIENT_CREDITS with {needed, balance}, or the Director's NO_CREDITS): opens the
 *  packs, unless the user closed them a moment ago. Returns whether it opened. */
export function openPacksForError(e: { code: string | null; details?: unknown }, now = Date.now()): boolean {
  if (e.code !== "INSUFFICIENT_CREDITS" && e.code !== "NO_CREDITS") return false;
  if (!current && now - closedAt < REOPEN_QUIET_MS) return false;
  const d = e.details && typeof e.details === "object" ? (e.details as Record<string, unknown>) : {};
  openPacks({ ...packsFromDirective({ reason: "out", needed: d.needed as number, balance: d.balance as number }), source: "error" });
  return true;
}

export const isLowBalance = (balance: number | null | undefined): boolean => typeof balance === "number" && balance < LOW_BALANCE_CREDITS;

/** The sheet's opening line for a request (null: no line, just the packs). */
export function packsLine(r: PacksRequest, fmt: (n: number) => string = (n) => n.toLocaleString("en-US")): string | null {
  if (r.note) return r.note;
  if (r.reason === "out") {
    if (r.needed !== undefined && r.balance !== undefined) return `That needs ${fmt(r.needed)} credits and you have ${fmt(r.balance)}. Everything you've made stays here; pick a pack to carry on.`;
    return "You're out of credits for that. Everything you've made stays here; pick a pack to carry on.";
  }
  if (r.reason === "low") return r.balance !== undefined ? `Running low: ${fmt(r.balance)} credits left.` : "Running low on credits.";
  return null;
}

// ------------------------------------------------------------------ the low-balance pill (dismissed per session)

const DISMISS_KEY = "cao.lowBalance.dismissed";
let dismissedHere = false;

/** Whether the "Running low" pill was dismissed in this browser session (sessionStorage; this page load without it). */
export function lowBalanceDismissed(): boolean {
  if (dismissedHere) return true;
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}
export function dismissLowBalance(): void {
  dismissedHere = true;
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* private mode: this page load only */
  }
  emit();
}

/** What the pill says for a balance, or null when it isn't shown (enough credits, unknown, or dismissed). */
export function lowBalanceText(balance: number | null | undefined, dismissed: boolean, fmt: (n: number) => string = (n) => n.toLocaleString("en-US")): string | null {
  if (dismissed || !isLowBalance(balance)) return null;
  return balance! <= 0 ? "Out of credits" : `Running low: ${fmt(balance!)} credits left`;
}

/** For tests. */
export function resetPacks(): void {
  current = null;
  opens = 0;
  closedAt = 0;
  dismissedHere = false;
  subs.clear();
}
